import { gql, useMutation } from '@apollo/client';
import ConfirmationModal from '@ovComponents/2-component/confirmationModal/confirmationModal';
import { FETCH_ACCOUNTS } from '@ovComponents/4-module/accountsSummary/AccountsSummary';
import { FETCH_USER } from '@pages/ClientDetail/fetchUser.query';
import { useToast } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { usePermission } from '../../../hooks/usePermission';

export const CLOSE_ACCOUNT = gql`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const CloseAccountModal = ({
  handleClose,
  open,
  accountId,
}: {
  accountId: string;
  handleClose: () => void;
  open: boolean;
}) => {
  const { t } = useTranslation(['accountDetail']);
  const { enqueueToast } = useToast();
  const canReadClientLowRiskPii = usePermission('read:client_low_risk_pii');
  const canReadAccountNumber = usePermission('read:account_number');
  const canReadClientHighRiskPii = usePermission('read:client_high_risk_pii');
  const canReadClientBilling = usePermission('read:client_billing');
  const canReadClientSuitability = usePermission('read:client_suitability');
  const canReadIdVerification = usePermission('read:id_verification');
  const canReadFraudRiskBasic = usePermission('read:fraud_risk_basic');
  const navigate = useNavigate();

  const closeAccountData = (): void => {
    handleClose();
    navigate(-1);
    enqueueToast(t('accountsDetail:closeAccountDialog.success'), {
      variant: 'success',
    });
  };

  const [closeAccountMutation] = useMutation(CLOSE_ACCOUNT, {
    variables: {
      input: {
        accountId,
        inactiveReason: 'OTHER',
      },
      skipErrorHandler: true,
    },
    refetchQueries: [
      FETCH_ACCOUNTS({ canReadAccountNumber, canReadClientLowRiskPii }),
      FETCH_USER({
        canReadClientLowRiskPii,
        canReadClientHighRiskPii,
        canReadClientBilling,
        canReadClientSuitability,
        canReadIdVerification,
        canReadFraudRiskBasic,
      }),
    ],
    onCompleted: closeAccountData,
  });

  return (
    <ConfirmationModal
      title={t('accountsDetail:closeAccountDialog.title')}
      bodyText={t('accountsDetail:closeAccountDialog.subtitle')}
      open={open}
      maxWidth='sm'
      confirmButton={t('accountsDetail:closeAccountDialog.continueButton')}
      cancelButton={t('accountsDetail:closeAccountDialog.cancelButton')}
      onConfirm={() => closeAccountMutation()}
      onCancel={handleClose}
    />
  );
};

export default CloseAccountModal;
